import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useAiPrompts } from '@/features/ai-prompts/api';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Dialog } from '@/components/ui/Dialog';
import type { CodePrompt } from '@honeycomb/shared';

export const Route = createFileRoute('/_authed/ai/prompts/')({
  component: AiPromptsPage,
});

const SLUG_LABELS: Record<CodePrompt['slug'], string> = {
  rules: 'Rules',
  generation: 'Generation',
  editing: 'Editing',
  vision: 'Vision',
  'image-gen': 'Image Generation',
};

function AiPromptsPage() {
  const { data, isLoading, isError, error } = useAiPrompts();
  const prompts = data?.prompts ?? [];
  const [viewPrompt, setViewPrompt] = useState<CodePrompt | null>(null);

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <header className="mb-6">
        <h1 className="font-serif text-3xl leading-tight">AI prompts</h1>
        <p className="text-ink-soft text-sm mt-1">
          Prompts are bundled in code. Deploy a new version to update them.
        </p>
      </header>

      {isLoading ? (
        <div className="text-ink-soft">Loading…</div>
      ) : isError ? (
        <div className="text-red-700" role="alert">
          Failed to load prompts: {error instanceof Error ? error.message : 'unknown error'}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {prompts.map((prompt) => (
            <PromptCard
              key={prompt.slug}
              prompt={prompt}
              onView={() => setViewPrompt(prompt)}
            />
          ))}
        </div>
      )}

      {viewPrompt && (
        <PromptViewDialog prompt={viewPrompt} onClose={() => setViewPrompt(null)} />
      )}
    </div>
  );
}

function PromptCard({
  prompt,
  onView,
}: {
  prompt: CodePrompt;
  onView: () => void;
}) {
  return (
    <Card
      shadow="sm"
      className="p-4 flex flex-col gap-3"
      data-testid={`ai-prompt-card-${prompt.slug}`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-ink">{SLUG_LABELS[prompt.slug]}</span>
        <span
          className="font-mono text-xs text-ink-soft"
          data-testid={`ai-prompt-card-${prompt.slug}-version`}
        >
          v{prompt.version}
        </span>
      </div>
      <p className="text-xs text-ink-soft line-clamp-3 leading-relaxed">
        {prompt.content.slice(0, 120)}
        {prompt.content.length > 120 ? '…' : ''}
      </p>
      <div className="flex items-center gap-2 mt-auto">
        <Button
          variant="ghost"
          size="sm"
          onClick={onView}
          data-testid={`ai-prompt-card-${prompt.slug}-view`}
        >
          View
        </Button>
      </div>
    </Card>
  );
}

function PromptViewDialog({
  prompt,
  onClose,
}: {
  prompt: CodePrompt;
  onClose: () => void;
}) {
  return (
    <Dialog open size="lg" onClose={onClose}>
      <div data-testid="ai-prompt-view-dialog" className="flex flex-col gap-4">
        <div>
          <h2 className="font-serif text-2xl leading-tight">{SLUG_LABELS[prompt.slug]}</h2>
          <span className="font-mono text-xs text-ink-soft">v{prompt.version}</span>
        </div>
        <div
          className="max-h-[70vh] overflow-y-auto rounded-lg border"
          style={{ borderColor: 'var(--color-hairline)' }}
        >
          <pre className="font-mono text-sm whitespace-pre-wrap break-words p-4 leading-relaxed">
            {prompt.content}
          </pre>
        </div>
        <div className="flex justify-end mt-2">
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
